// ============================================================
// Full path:
//   D:\Thyroid Consultation Software\ThyroConsult Frontend\thyroconsult-frontend\src\i18n\websiteLanguages.js
//
// (Renamed from languages.js on 14 Oct 2026 so it is never mixed up with the
// BACKEND file src\config\languages.js — the list of languages a patient
// can save on the account.)
//
// THE ONE LIST of website languages (13 Oct 2026 — any-language version;
// 14 Oct 2026: six more languages switched on for the questionnaires).
// Every place that depends on the language (date names, the language
// dropdown, right-to-left pages, the name/address helper, script checks,
// extra fonts) reads it from here, so nothing else in the code says "Hindi".
//
// All 22 scheduled languages of India and their scripts are listed.
// A language appears in the website's language dropdown ONLY when it has a
// word file in DICT_LOADERS below.
//
// ── To add a language (e.g. Marathi) ─────────────────────────────────────
//   1. Make src\i18n\mr.js — a copy of hi.js with the same English keys and
//      Marathi values (export default {...}; export const PATTERNS = [...]).
//   2. Add one line to DICT_LOADERS:   mr: () => import('./mr'),
//   3. (Optional) the consent translation in src\content\consentTexts.js
//      (CONSENT_TRANSLATIONS) — until then the consents show in English.
//   Nothing else changes. Word files are loaded only when someone picks
//   that language, so more languages never slow the website down.
// ============================================================

// Scripts used for the scheduled languages of India.
//   test     — letters of this script in a text
//   fontFile — OPTIONAL: a font in public\fonts\ used only for this script's
//              letters, for scripts many phones / computers have no font for.
//              If the file is not there, nothing changes (the browser's own
//              font is used).
export const SCRIPTS = {
  Latn: { name: 'Latin', test: /[A-Za-z]/ },
  Deva: { name: 'Devanagari', test: /[\u0900-\u097F\uA8E0-\uA8FF]/ },
  Beng: { name: 'Bengali–Assamese', test: /[\u0980-\u09FF]/ },
  Guru: { name: 'Gurmukhi', test: /[\u0A00-\u0A7F]/ },
  Gujr: { name: 'Gujarati', test: /[\u0A80-\u0AFF]/ },
  Orya: { name: 'Odia', test: /[\u0B00-\u0B7F]/ },
  Taml: { name: 'Tamil', test: /[\u0B80-\u0BFF]/ },
  Telu: { name: 'Telugu', test: /[\u0C00-\u0C7F]/ },
  Knda: { name: 'Kannada', test: /[\u0C80-\u0CFF]/ },
  Mlym: { name: 'Malayalam', test: /[\u0D00-\u0D7F]/ },
  Olck: { name: 'Ol Chiki', test: /[\u1C50-\u1C7F]/, fontFile: 'NotoSansOlChiki-Regular.ttf', unicodeRange: 'U+1C50-1C7F' },
  Mtei: { name: 'Meetei Mayek', test: /[\uABC0-\uABFF\uAAE0-\uAAFF]/, fontFile: 'NotoSansMeeteiMayek-Regular.ttf', unicodeRange: 'U+AAE0-AAFF, U+ABC0-ABFF' },
  Arab: { name: 'Perso-Arabic', test: /[\u0600-\u06FF\u0750-\u077F\uFB50-\uFDFF\uFE70-\uFEFF]/ },
};

// Any Indian (non-Latin) letters at all.
export const INDIAN_SCRIPT_TEST = new RegExp(
  Object.entries(SCRIPTS).filter(([k]) => k !== 'Latn').map(([, s]) => s.test.source).join('|')
);

// code: as saved on the account (patients.preferred_language) — the SAME codes
// as the BACKEND's src\config\languages.js (e.g. Manipuri is mnib / mnim)
// native: the name in its own letters (shown in the dropdown)
// locale: for dates ("13 अक्टूबर 2026"); dir: 'rtl' for right-to-left
export const LANGUAGES = [
  { code: 'en',  name: 'English',   native: 'English',   script: 'Latn', locale: 'en-IN' },
  { code: 'hi',  name: 'Hindi',     native: 'हिन्दी',     script: 'Deva', locale: 'hi-IN' },
  { code: 'mr',  name: 'Marathi',   native: 'मराठी',      script: 'Deva', locale: 'mr-IN' },
  { code: 'ne',  name: 'Nepali',    native: 'नेपाली',     script: 'Deva', locale: 'ne-IN' },
  { code: 'kok', name: 'Konkani',   native: 'कोंकणी',     script: 'Deva', locale: 'kok-IN' },
  { code: 'mai', name: 'Maithili',  native: 'मैथिली',     script: 'Deva', locale: 'mai-IN' },
  { code: 'doi', name: 'Dogri',     native: 'डोगरी',      script: 'Deva', locale: 'doi-IN' },
  { code: 'brx', name: 'Bodo',      native: 'बड़ो',        script: 'Deva', locale: 'brx-IN' },
  { code: 'sa',  name: 'Sanskrit',  native: 'संस्कृतम्',   script: 'Deva', locale: 'sa-IN' },
  { code: 'bn',  name: 'Bengali',   native: 'বাংলা',      script: 'Beng', locale: 'bn-IN' },
  { code: 'as',  name: 'Assamese',  native: 'অসমীয়া',    script: 'Beng', locale: 'as-IN' },
  { code: 'pa',  name: 'Punjabi',   native: 'ਪੰਜਾਬੀ',     script: 'Guru', locale: 'pa-IN' },
  { code: 'gu',  name: 'Gujarati',  native: 'ગુજરાતી',    script: 'Gujr', locale: 'gu-IN' },
  { code: 'or',  name: 'Odia',      native: 'ଓଡ଼ିଆ',       script: 'Orya', locale: 'or-IN' },
  { code: 'ta',  name: 'Tamil',     native: 'தமிழ்',      script: 'Taml', locale: 'ta-IN' },
  { code: 'te',  name: 'Telugu',    native: 'తెలుగు',     script: 'Telu', locale: 'te-IN' },
  { code: 'kn',  name: 'Kannada',   native: 'ಕನ್ನಡ',      script: 'Knda', locale: 'kn-IN' },
  { code: 'ml',  name: 'Malayalam', native: 'മലയാളം',    script: 'Mlym', locale: 'ml-IN' },
  { code: 'sat', name: 'Santali',   native: 'ᱥᱟᱱᱛᱟᱲᱤ',   script: 'Olck', locale: 'sat-IN' },
  // Manipuri: two account codes, as in the backend list (src\config\languages.js)
  { code: 'mnib', name: 'Manipuri (Bengali script)', native: 'মৈতৈলোন্', script: 'Beng', locale: 'mni-IN' },
  { code: 'mnim', name: 'Manipuri (Meitei script)',  native: 'ꯃꯤꯇꯩꯂꯣꯟ',  script: 'Mtei', locale: 'mni-IN' },
  { code: 'ur',  name: 'Urdu',      native: 'اردو',       script: 'Arab', locale: 'ur-IN', dir: 'rtl' },
  { code: 'ks',  name: 'Kashmiri',  native: 'کٲشُر',      script: 'Arab', locale: 'ks-IN', dir: 'rtl' },
  { code: 'sd',  name: 'Sindhi',    native: 'سنڌي',       script: 'Arab', locale: 'sd-IN', dir: 'rtl' },
];

// Word files — ONE LINE PER LANGUAGE that the website can be shown in.
// (Each file: export default { 'English text': 'translation', … };
//  export const PATTERNS = [ [regex, (m, tr) => text], … ];)
// A language listed with NOT_YET_TRANSLATED is in the dropdown before it
// has any approved words (picking it shows the pages in English); the first
// approve by scripts\i18n.js switches its line to the real word file.
// Marathi, Gujarati, Tamil, Telugu, Kannada and Malayalam (14 Oct 2026):
// their questionnaire wording is live (owner decision); everything not yet in
// their word file shows in English and is added over time.
// eslint-disable-next-line no-unused-vars
const NOT_YET_TRANSLATED = () => Promise.resolve({ default: {}, PATTERNS: [] });

export const DICT_LOADERS = {
  hi: () => import('./hi'),
  ml: () => import('./ml'),
  kn: () => import('./kn'),
  te: () => import('./te'),
  mr: () => import('./mr'),
  gu: () => import('./gu'),
  ta: () => import('./ta'),
  // Questionnaire wording only, on staging for testing (14 Oct 2026)
  pa: () => import('./pa'),
  bn: () => import('./bn'),
  or: () => import('./or'),
  as: () => import('./as'),
  ne: () => import('./ne'),
  mnib: () => import('./mnib'),
  mnim: () => import('./mnim'),
};

const BY_CODE = Object.fromEntries(LANGUAGES.map(l => [l.code, l]));

export const languageInfo = (code) => BY_CODE[code] || BY_CODE.en;
export const scriptOf = (code) => SCRIPTS[languageInfo(code).script] || SCRIPTS.Latn;
export const isRtl = (code) => languageInfo(code).dir === 'rtl';
